/* globals StaxJs */

import {
	GET_PAYMENT_TOKEN_COMPLETED,
	GENERAL_ERROR,
	CARD_FORM_READY,
	CARD_FORM_ERROR,
	CARD_VALIDATION_STATUS,
} from '../Checkout/common.mjs';
import { registerPaymentProcessor } from '../Checkout/main.mjs';

let staxJs;

/**
 * @param {{staxNumberId: string, staxCvvId: string, method: string}} data The data
 * @param {object} options The options
 * @param {(key: string, value: any) => void} sendSub The send sub callback
 * @returns {Promise<{key: string, value?: any}>} A promise with the result
 */
export function handleDomReady(
	{ staxNumberId, staxCvvId, method },
	options,
	sendSub,
) {
	try {
		return new Promise((resolve, reject) => {
			/**
			 * @param {string} cardType The card type
			 * @returns {string} The new card type
			 */
			function cardTypeConverter(cardType) {
				if (cardType === 'master') {
					return 'mastercard';
				}

				if (cardType === 'american_express') {
					return 'amex';
				}

				return cardType;
			}

			try {
				if (method === 'bank') {
					// @ts-ignore
					staxJs = new StaxJs(options.staxPaymentsToken, {});
					resolve({ key: CARD_FORM_READY });
					return;
				}

				if (method === 'card') {
					// @ts-ignore
					staxJs = new StaxJs(options.staxPaymentsToken, {
						number: {
							id: staxNumberId,
							placeholder: 'Card number',
							style: 'padding: 0.75em; width:100%; font-size: 1rem; color: rgb(119, 119, 119); font-weight: 600; font-family: Roboto, Helvetica, sans-serif;',
						},
						cvv: {
							id: staxCvvId,
							placeholder: 'CVC',
							style: 'padding: 0.75em; width:100%; font-size: 1rem; color: rgb(119, 119, 119); font-weight: 600; font-family: Roboto, Helvetica, sans-serif;',
						},
					});
				}

				staxJs.on('card_form_complete', (message) => {
					message.cardType = cardTypeConverter(message.cardType);
					sendSub(CARD_VALIDATION_STATUS, message);
				});

				staxJs.on('card_form_incomplete', (message) => {
					message.cardType = cardTypeConverter(message.cardType);

					const cardExp = /** @type {HTMLInputElement} */ (
						document.getElementById('engagecore-card-exp')
					);

					if (!message.validNumber) {
						message.cardType = undefined;
					} else if (cardExp && cardExp.value === '') {
						cardExp.focus();
					}

					sendSub(CARD_VALIDATION_STATUS, message);
				});

				staxJs
					.showCardForm()
					.then(() => {
						resolve({ key: CARD_FORM_READY });
					})
					.catch((err) => {
						reject({ key: CARD_FORM_ERROR, value: err });
					});
			} catch (error) {
				reject({ key: GENERAL_ERROR, value: error });
			}
		});
	} catch (error) {
		return Promise.reject({ key: GENERAL_ERROR, value: error });
	}
}

/**
 * @typedef StaxPaymentData
 * @property { "card" | "bank" } method The payment method
 * @property {string} month The card expiration month
 * @property {string} year The card expiration year
 * @property {string} bank_name The bank name
 * @property {string} bank_account The bank account number
 * @property {string} bank_routing The bank routing number
 * @property { "checking" | "savings" } bank_type The bank type
 * @property { "personal" | "business" } bank_holder_type The bank holder type
 */

/**
 * Get the payment token
 * @param {StaxPaymentData} paymentData The payment data
 * @returns {Promise<{key: string, value?: any}>} The payment token
 */
export function getToken(paymentData) {
	try {
		return new Promise((resolve, reject) => {
			if (staxJs) {
				staxJs
					.tokenize(paymentData)
					.then((result) => {
						resolve({
							key: GET_PAYMENT_TOKEN_COMPLETED,
							value: {
								staxPaymentMethod: result,
							},
						});
					})
					.catch((error) => {
						reject({
							key: GET_PAYMENT_TOKEN_COMPLETED,
							value: {
								error:
									typeof error === 'object'
										? JSON.stringify(error)
										: String(error),
							},
						});
					});
			} else {
				reject({
					key: GET_PAYMENT_TOKEN_COMPLETED,
					value: {
						error: 'Stax element has not been initialized yet',
					},
				});
			}
		});
	} catch (error) {
		return Promise.reject({ key: GENERAL_ERROR, value: error });
	}
}

registerPaymentProcessor({
	name: 'Stax',
	getToken,
	handleDomReady,
});
